/**
 * Skeletons for the portfolio pages (#4765): the same header-and-tiles
 * geometry as the loaded page, shimmering, so content lands without reflow
 * and nothing reads as a spinner. Mirrors the shimmer of
 * `../math-ide-agent/MathIdeAgentExperienceSkeleton.tsx`.
 */

import styled, {css, keyframes} from 'styled-components';
import {Grid, RULE, Thumb, TileBody} from './portfolioTiles';

/** How many tiles the grid skeleton shows before the real entries arrive. */
const SKELETON_TILE_COUNT = 3;

/**
 * The owner's page box. The page and its skeletons share it, so content
 * lands exactly where the skeleton stood.
 */
export const PortfolioPageFrame = styled.main`
  max-width: 1120px;
  margin: 0 auto;
  padding: 40px 28px 72px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: var(--course-fg, #111827);
`;

/**
 * The owner's header row as a skeleton: the share button and the published name.
 *
 * @returns The header skeleton, to render above {@link PortfolioGridSkeleton}.
 */
export function PortfolioHeaderSkeleton() {
  return (
    <HeaderSkeleton aria-hidden>
      <Bone $height={30} $width="172px" $radius={999} />
      <Bone $height={20} $width="260px" />
    </HeaderSkeleton>
  );
}

/**
 * The tile grid as a skeleton, on the page's own grid.
 *
 * @returns The grid skeleton.
 */
export function PortfolioGridSkeleton() {
  return (
    <Grid aria-hidden>
      {Array.from({length: SKELETON_TILE_COUNT}, (_, i) => (
        <TileSkeleton key={i}>
          <Thumb>
            <ThumbFill />
          </Thumb>
          <TileBody>
            <Bone $height={18} $width="62%" />
            <Bone $height={14} $width="38%" />
          </TileBody>
        </TileSkeleton>
      ))}
    </Grid>
  );
}

/**
 * The whole owner's portfolio page as a skeleton, so it stands in before the
 * route's code or data arrives.
 *
 * @returns The page skeleton.
 */
export function PortfolioPageSkeleton() {
  return (
    <PortfolioPageFrame>
      <PortfolioHeaderSkeleton />
      <PortfolioGridSkeleton />
    </PortfolioPageFrame>
  );
}

/**
 * The whole read-only app view as a skeleton: its bar and the app frame.
 *
 * @returns The page skeleton.
 */
export function PortfolioAppPageSkeleton() {
  return (
    <SkeletonShell aria-hidden>
      <SkeletonBar>
        <Bone $height={28} $width="3%" $radius={6} />
        <Bone $height={18} $width="20%" />
      </SkeletonBar>
      <SkeletonBody>
        <PortfolioAppSkeleton />
      </SkeletonBody>
    </SkeletonShell>
  );
}

/**
 * The read-only app view's skeleton: a bar and an app-shaped frame.
 *
 * @returns The skeleton, to render inside `PortfolioAppRoute`'s layout.
 */
export function PortfolioAppSkeleton() {
  return (
    <AppSkeleton aria-hidden>
      <Bone $height={72} $width="100%" $radius={16} />
      <Bone $height={14} $width="30%" />
      <Bone $height={56} $width="100%" $radius={10} />
      <Bone $height={56} $width="100%" $radius={10} />
      <Bone $height={14} $width="30%" />
      <Bone $height={140} $width="100%" $radius={12} />
    </AppSkeleton>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const shimmerStyle = css`
  background: linear-gradient(90deg, #eceef1 25%, #f5f6f8 37%, #eceef1 63%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;

const Bone = styled.div<{$height: number; $width: string; $radius?: number}>`
  height: ${({$height}) => $height}px;
  width: ${({$width}) => $width};
  max-width: 100%;
  border-radius: ${({$radius}) => $radius ?? 6}px;
  ${shimmerStyle}
`;

/* Same row as the page's header: share button left, name right. */
const HeaderSkeleton = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 28px;
`;

/* Same frame as a tile's card link. */
const TileSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${RULE};
  border-radius: 14px;
  overflow: hidden;
`;

const ThumbFill = styled.div`
  position: absolute;
  inset: 0;
  ${shimmerStyle}
`;

/* Same shell as the read-only app view. */
const SkeletonShell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100dvh;
  background: #f9fafb;
`;

const SkeletonBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
`;

const SkeletonBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  justify-content: center;
  padding: 20px;
`;

const AppSkeleton = styled.div`
  width: min(640px, 100%);
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px 22px;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #fff;
`;
